import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ArrowRight, Truck, MapPin, Tag, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { track } from "../lib/track";

const titleCaseCity = (s) => (s || "").trim().replace(/[A-Za-z]+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fulfillment, setFulfillment] = useState("ship"); // "ship" | "pickup"
  const [promoInput, setPromoInput] = useState("");
  const [coupon, setCoupon] = useState(null); // { code, discount, type, value }
  const [applying, setApplying] = useState(false);
  const [ship, setShip] = useState({
    name: user?.name || "",
    email: user?.email || "",
    address: "",
    city: "",
    state: "CA",
    zip: "",
  });

  const isPickup = fulfillment === "pickup";
  const discount = coupon ? coupon.discount : 0;
  const discountedSubtotal = Math.max(subtotal - discount, 0);
  const shipping = isPickup || discountedSubtotal >= 35 || subtotal === 0 ? 0 : 4.5;
  const total = discountedSubtotal + shipping;

  const applyCoupon = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.get(`/coupon/${encodeURIComponent(code)}`, { params: { subtotal } });
      setCoupon(data);
      toast.success(`Code ${data.code} applied. You saved $${data.discount.toFixed(2)}`);
    } catch (e) {
      setCoupon(null);
      toast.error(formatApiError(e));
    } finally {
      setApplying(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setPromoInput("");
  };

  const handleCheckout = async () => {
    if (!items.length) return;
    if (!ship.name || !ship.email) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!isPickup && (!ship.address || !ship.city || !ship.zip)) {
      toast.error("Please fill in shipping information");
      return;
    }
    setLoading(true);
    try {
      track("begin_checkout", { items: items.length, subtotal });
      const { data } = await api.post("/checkout/session", {
        items: items.map(({ product_id, quantity }) => ({ product_id, quantity })),
        origin_url: window.location.origin,
        shipping: ship,
        fulfillment,
        coupon_code: coupon ? coupon.code : null,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  if (!items.length) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="empty-cart">
        <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Your cart</div>
        <h1 className="font-display text-5xl text-darkRoast">It's empty in here.</h1>
        <p className="mt-4 text-earth">Pick up a few packets to get started.</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-8 bg-darkRoast text-cream px-6 py-3 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors">
          Browse seeds <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="cart-page">
      <div className="mb-12">
        <div className="text-xs tracking-wider-2 uppercase text-sage mb-3">Your cart</div>
        <h1 className="font-display text-5xl text-darkRoast">Almost in the ground.</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-6">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-5 border-b border-darkRoast/10 pb-6" data-testid={`cart-item-${it.product_id}`}>
              <Link to={`/product/${it.product_id}`} className="w-24 h-24 sm:w-32 sm:h-32 flex-shrink-0 bg-surface border border-darkRoast/10 overflow-hidden rounded-sm">
                <img src={it.image_url} alt={it.name} className="w-full h-full object-cover" />
              </Link>
              <div className="flex-1 flex flex-col">
                <Link to={`/product/${it.product_id}`} className="font-display text-xl text-darkRoast leading-tight hover:text-sage">{it.name}</Link>
                <div className="text-sm text-earth mt-1">{it.packet_size_g}g packet · ${it.price.toFixed(2)}</div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center border border-darkRoast/20 rounded-full text-sm">
                    <button onClick={() => updateQty(it.product_id, it.quantity - 1)} className="px-3 py-1.5 hover:text-sage" data-testid={`cart-dec-${it.product_id}`}>−</button>
                    <span className="px-3">{it.quantity}</span>
                    <button onClick={() => updateQty(it.product_id, it.quantity + 1)} disabled={Number.isFinite(it.stock) && it.quantity >= it.stock} className="px-3 py-1.5 hover:text-sage disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`cart-inc-${it.product_id}`}>+</button>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-medium text-darkRoast">${(it.price * it.quantity).toFixed(2)}</span>
                    <button onClick={() => removeItem(it.product_id)} className="text-earth hover:text-terracotta" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div>
          <div className="bg-surface p-8 rounded-sm">
            <h2 className="font-display text-2xl text-darkRoast mb-5">How would you like it?</h2>

            <div className="grid grid-cols-2 gap-2 mb-6" data-testid="fulfillment-toggle">
              <button
                type="button"
                onClick={() => setFulfillment("ship")}
                className={`flex flex-col items-start gap-1 p-3 rounded-sm border text-left transition-colors ${!isPickup ? "border-sage bg-sage/10" : "border-darkRoast/15 hover:border-sage"}`}
                data-testid="fulfillment-ship"
              >
                <Truck className="w-4 h-4 text-sage" strokeWidth={1.5} />
                <span className="text-xs tracking-wider-2 uppercase text-darkRoast">Ship to me</span>
                <span className="text-[11px] text-earth">Flat $4.50 · free over $35</span>
              </button>
              <button
                type="button"
                onClick={() => setFulfillment("pickup")}
                className={`flex flex-col items-start gap-1 p-3 rounded-sm border text-left transition-colors ${isPickup ? "border-sage bg-sage/10" : "border-darkRoast/15 hover:border-sage"}`}
                data-testid="fulfillment-pickup"
              >
                <MapPin className="w-4 h-4 text-sage" strokeWidth={1.5} />
                <span className="text-xs tracking-wider-2 uppercase text-darkRoast">Local pickup</span>
                <span className="text-[11px] text-earth">Plymouth, CA · free</span>
              </button>
            </div>

            {isPickup && (
              <p className="text-xs text-earth bg-cream rounded-sm p-3 mb-5" data-testid="pickup-note">
                Free pickup in Plymouth, CA. After you order, we'll reach out by email to arrange a time.
              </p>
            )}

            <div className="space-y-3 mb-6">
              {(isPickup
                ? [["name", "Full name"], ["email", "Email"]]
                : [["name", "Full name"], ["email", "Email"], ["address", "Street address"], ["city", "City"], ["state", "State"], ["zip", "ZIP"]]
              ).map(([k, label]) => (
                <input
                  key={k}
                  value={ship[k]}
                  onChange={(e) => setShip({ ...ship, [k]: e.target.value })}
                  onBlur={k === "city" ? (e) => setShip((s) => ({ ...s, city: titleCaseCity(e.target.value) })) : undefined}
                  placeholder={label}
                  className="w-full bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm focus:outline-none focus:border-sage placeholder:text-earth/60"
                  data-testid={`ship-${k}`}
                />
              ))}
            </div>

            <div className="space-y-2 text-sm pb-4 border-b border-darkRoast/10">
              <div className="flex justify-between"><span className="text-earth">Subtotal</span><span data-testid="cart-subtotal">${subtotal.toFixed(2)}</span></div>
              {coupon && (
                <div className="flex justify-between text-sage" data-testid="cart-discount-row">
                  <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" strokeWidth={1.5} /> {coupon.code}</span>
                  <span data-testid="cart-discount">−${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-earth">{isPickup ? "Pickup" : "Shipping"}</span><span>{shipping === 0 ? "Free" : `$${shipping.toFixed(2)}`}</span></div>
              {!isPickup && discountedSubtotal < 35 && subtotal > 0 && <div className="text-xs text-terracotta">Add ${(35 - discountedSubtotal).toFixed(2)} more for free shipping.</div>}
            </div>

            <div className="py-4 border-b border-darkRoast/10">
              {coupon ? (
                <div className="flex items-center justify-between bg-sage/10 rounded-sm px-3 py-2" data-testid="applied-coupon">
                  <span className="text-xs tracking-wider-2 uppercase text-sage flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" strokeWidth={1.5} /> {coupon.code} applied
                  </span>
                  <button onClick={removeCoupon} className="text-earth hover:text-terracotta" data-testid="remove-coupon-btn" aria-label="Remove coupon">
                    <X className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    value={promoInput}
                    onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === "Enter") applyCoupon(); }}
                    placeholder="Promo code"
                    className="flex-1 bg-transparent border-b border-darkRoast/20 px-1 py-2 text-sm uppercase focus:outline-none focus:border-sage placeholder:text-earth/60 placeholder:normal-case"
                    data-testid="promo-input"
                  />
                  <button
                    onClick={applyCoupon}
                    disabled={applying || !promoInput.trim()}
                    className="px-4 text-xs tracking-wider-2 uppercase border border-darkRoast/20 rounded-full hover:border-sage hover:text-sage transition-colors disabled:opacity-50"
                    data-testid="apply-promo-btn"
                  >
                    {applying ? "…" : "Apply"}
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-between items-baseline pt-4 mb-6">
              <span className="font-display text-2xl text-darkRoast">Total</span>
              <span className="font-display text-3xl text-darkRoast" data-testid="cart-total">${total.toFixed(2)}</span>
            </div>

            <button
              disabled={loading}
              onClick={handleCheckout}
              className="w-full bg-darkRoast text-cream py-4 rounded-full text-xs tracking-wider-2 uppercase hover:bg-sage transition-colors disabled:opacity-60"
              data-testid="checkout-btn"
            >
              {loading ? "Redirecting…" : "Checkout with Stripe"}
            </button>
            <p className="text-[10px] text-earth/70 text-center mt-3 tracking-wider-2 uppercase">Secure payment</p>
          </div>
        </div>
      </div>
    </div>
  );
}
